import { OrbitControls } from '@react-three/drei';
import type {} from '@react-three/fiber';
import type { BackgammonPosition } from '@chessmate/game-engine';
import type { Turn } from '@chessmate/protocol';
import { useMemo } from 'react';
import * as THREE from 'three';
import { ProjectionReporter } from './BoardProjection.js';
import type { PlaneCorners } from './projection.js';
import { Table } from './Table.js';
import {
  type BoardTheme,
  DEFAULT_BOARD_THEME,
  DEFAULT_TABLE_THEME,
  type TableTheme,
} from './themes.js';

/**
 * The backgammon board — a 24-point scene, deliberately NOT a variant flag on
 * the 8×8 `Board`. The chess assumptions (`squareToVec3`'s a1-at-a-corner,
 * `SquareOverlay`'s 64-button grid, FEN piece meshes) run too deep to branch;
 * backgammon reads its own position type and lays out points, a bar, and an
 * off tray. See docs/proposals/backgammon-wiring.md, slice 2.
 *
 * **Orientation is the trap this module exists to pin.** The viewer sits at the
 * white player's seat. White moves 24→1 with home board 1–6, so from this seat
 * the near row (bottom of the screen) carries points 12…1 left-to-right, the
 * far row carries 13…24, and the whole path runs counterclockwise: 24→19 along
 * the far right, 18→13 across the far left, 12→7 down the near left, 6→1 into
 * the near right corner. Every one of those facts is asserted in
 * `BackgammonBoard.test.ts` as coordinate data, because a sign error here
 * lights the board mirrored and nothing else would catch it.
 *
 * **Taps do not live here.** This is the render half of slice 2; the point-tap
 * layer is app-side work in slice 3, on the model of (but not reusing)
 * `SquareOverlay`. Nothing in this file takes an onClick.
 *
 * **Checkers stack fully rather than capping at 5 with a count glyph.** A count
 * would need scene text (a font dependency the 3D package does not carry), and
 * a kid counting a tower of checkers is doing exactly the arithmetic the game
 * teaches. The stack helper is exported and height-tested so a later count
 * glyph, if ever wanted, starts from measured ground.
 */

// 6 points per half-row at 0.6 wide (7.2) plus a 0.8 bar = 8.0 total span,
// matching the chess board's [-4, 4] footprint so the shared 13×13 Table fits.
export const BACKGAMMON_POINT_WIDTH = 0.6;
export const BACKGAMMON_BAR_WIDTH = 0.8;
/** Half the bar's width: the innermost points' centers sit this far from x=0. */
export const BACKGAMMON_INNER_X = BACKGAMMON_BAR_WIDTH / 2 + BACKGAMMON_POINT_WIDTH / 2; // 0.7
/** How deep the point triangles reach from the board edge. */
export const BACKGAMMON_POINT_LENGTH = 3.2;
/** Row centers: near row (points 1–12) and far row (points 13–24). */
export const BACKGAMMON_NEAR_Z = -1.75;
/** The playing surface's near and far edges, where the point triangles start. */
export const BACKGAMMON_EDGE_Z = 3.5;

/** Screen-right extent the DOM tap grid covers: from the left edge of point 12/13
 *  (u = -4.0) to the far side of the off tray (u = 5.3, the frame's right edge).
 *  The grid's column template is built from the same widths, so its cells line up
 *  with the points (see `BackgammonTapLayer`). */
export const BACKGAMMON_OVERLAY_U = { left: -4.0, right: 5.3 } as const;

/** The board-plane corners the tap grid's TL, TR, BR, BL map to (world x = -u). */
export const BACKGAMMON_PLANE: PlaneCorners = {
  tl: [-BACKGAMMON_OVERLAY_U.left, BACKGAMMON_EDGE_Z],
  tr: [-BACKGAMMON_OVERLAY_U.right, BACKGAMMON_EDGE_Z],
  br: [-BACKGAMMON_OVERLAY_U.right, -BACKGAMMON_EDGE_Z],
  bl: [-BACKGAMMON_OVERLAY_U.left, -BACKGAMMON_EDGE_Z],
};
export const BACKGAMMON_FAR_Z = 1.75;

/** Checker geometry: a flat cylinder. One geometry shared by every checker. */
export const BACKGAMMON_CHECKER_RADIUS = 0.26;
export const BACKGAMMON_CHECKER_HEIGHT = 0.12;

/**
 * Where point `point` (1–24) sits ACROSS the screen, as a signed distance from
 * the bar: negative is the player's left, positive their right.
 *
 * This is the standard board, from white's seat (the module comment's table):
 *   near row  12 11 10 9 8 7 | bar | 6 5 4 3 2 1
 *   far row   13 14 15 16 17 18 | bar | 19 20 21 22 23 24
 * so the points beside the bar are 6, 7, 18 and 19, and the corners are 1, 12,
 * 13 and 24. (Quadrants 7–12 and 19–24 used to be laid out in the opposite
 * order, which put the 8-point second from the edge and white's back checkers
 * beside the bar; the old tests pinned that. See
 * docs/audits/2026-10-15-backgammon-board-layout.md.)
 */
export function backgammonPointU(point: number): number {
  const step = BACKGAMMON_POINT_WIDTH;
  if (point <= 6) return BACKGAMMON_INNER_X + (6 - point) * step; // 6 by the bar … 1 at the right edge
  if (point <= 12) return -(BACKGAMMON_INNER_X + (point - 7) * step); // 7 by the bar … 12 at the left edge
  if (point <= 18) return -(BACKGAMMON_INNER_X + (18 - point) * step); // 18 by the bar … 13 at the left edge
  return BACKGAMMON_INNER_X + (point - 19) * step; // 19 by the bar … 24 at the right edge
}

/** World (x, z) of the CENTER of point `point` (1–24).
 *
 *  The camera is on the -z side, so SCREEN-RIGHT is world -x: x = -u. (The board
 *  used to be placed with x = +u, i.e. drawn mirrored: the home board bottom-LEFT
 *  and the off tray on the left rim.) */
export function backgammonPointXZ(point: number): [number, number] {
  const z = point <= 12 ? BACKGAMMON_NEAR_Z : BACKGAMMON_FAR_Z;
  return [-backgammonPointU(point), z];
}

/**
 * The colour of the LIGHT triangles: the board theme's light colour pulled 40% of
 * the way toward its dark one. The light triangles used to share the felt's colour
 * (`boardTheme.light`), so they were invisible and only every other point showed;
 * this keeps them lighter than the dark triangles and clearly off the felt.
 */
export function backgammonLightTriangleColor(theme: Pick<BoardTheme, 'light' | 'dark'>): string {
  return `#${new THREE.Color(theme.light).lerp(new THREE.Color(theme.dark), 0.4).getHexString()}`;
}

/** True when point `point` renders in the dark triangle colour. Point 1 is
 *  dark; alternation follows the point number around the loop. */
export function backgammonPointIsDark(point: number): boolean {
  return point % 2 === 1;
}

/** Height of the `index`-th checker (0-based) in a stack. Delta (0.13) exceeds
 *  the checker height (0.12) so stacked meshes never interpenetrate. */
export function backgammonStackY(index: number): number {
  return 0.06 + index * (BACKGAMMON_CHECKER_HEIGHT + 0.01);
}

/** World (x, z) of the `index`-th (0-based) checker on the bar. White waits on
 *  the far half (their re-entry window is 19–24), black on the near half —
 *  each queue sits at the end of the board it must come back through. */
export function backgammonBarXZ(color: Turn, index: number): [number, number] {
  const z = color === 'w' ? BACKGAMMON_FAR_Z : BACKGAMMON_NEAR_Z;
  return [0, z + (color === 'w' ? 1 : -1) * (0.55 + index * 0.3)];
}

/**
 * World (x, z) of the `index`-th (0-based) borne-off checker in the off tray
 * on the frame's right rim: two columns of 8, white near, black far. Fifteen
 * checkers laid flat in one column would tower 1.95 units; two columns of 8
 * keep the tray under 1.1 and read as "the scoreboard", which is what it is.
 *
 * The tray is why the backgammon frame is 10.6 wide, not the chess board's
 * 9.5: two checker columns (radius 0.26) need 1.04 of rim, and real boards
 * have this asymmetric right side too. The 13-wide Table still fits.
 */
export const BACKGAMMON_FRAME_WIDTH = 10.6;
export const BACKGAMMON_OFF_COLUMNS = 8;
/** Row pitch in the tray: tighter than the 0.52 no-touch spacing, because
 *  eight rows at no-touch pitch span 3.64 and spill across the board's
 *  midline. 0.44 lets the disks touch slightly — which is what a real
 *  bear-off tray looks like, checkers leaned against each other. */
export const BACKGAMMON_OFF_PITCH = 0.44;
export function backgammonOffXZ(color: Turn, index: number): [number, number] {
  const col = Math.floor(index / BACKGAMMON_OFF_COLUMNS);
  const row = index % BACKGAMMON_OFF_COLUMNS;
  // The tray is on the player's RIGHT; screen-right is world -x (see backgammonPointXZ).
  const x = -(4.45 + col * 0.55);
  const baseZ = color === 'w' ? BACKGAMMON_NEAR_Z : BACKGAMMON_FAR_Z;
  const z = baseZ + (row - (BACKGAMMON_OFF_COLUMNS - 1) / 2) * BACKGAMMON_OFF_PITCH;
  return [x, z];
}

export interface BackgammonBoardProps {
  readonly position: BackgammonPosition;
  readonly boardTheme?: BoardTheme;
  readonly tableTheme?: TableTheme;
  readonly whiteColor?: string;
  readonly blackColor?: string;
  /** Receives the CSS `matrix3d` that registers the DOM tap grid with this board
   *  as the camera sees it (see `projection.ts`). */
  readonly onOverlayTransform?: (matrix3d: string) => void;
}

export function BackgammonBoard({
  position,
  boardTheme = DEFAULT_BOARD_THEME,
  tableTheme = DEFAULT_TABLE_THEME,
  whiteColor = '#f3e9d2',
  blackColor = '#2e2a3b',
  onOverlayTransform,
}: BackgammonBoardProps) {
  // Shared geometry, built once per mount: one triangle, one checker, two
  // surface halves, one frame. Only materials vary.
  const triangleGeometry = useMemo(() => {
    const half = BACKGAMMON_POINT_WIDTH / 2;
    const shape = new THREE.Shape();
    shape.moveTo(-half, 0);
    shape.lineTo(half, 0);
    shape.lineTo(0, BACKGAMMON_POINT_LENGTH);
    shape.closePath();
    return new THREE.ExtrudeGeometry(shape, { depth: 0.02, bevelEnabled: false });
  }, []);
  const checkerGeometry = useMemo(
    () =>
      new THREE.CylinderGeometry(
        BACKGAMMON_CHECKER_RADIUS,
        BACKGAMMON_CHECKER_RADIUS,
        BACKGAMMON_CHECKER_HEIGHT,
        24,
      ),
    [],
  );
  const halfGeometry = useMemo(() => new THREE.BoxGeometry(3.6, 0.15, 7), []);

  const lightMat = useMemo(
    () => new THREE.MeshStandardMaterial({ color: boardTheme.light, roughness: 0.8 }),
    [boardTheme],
  );
  const darkMat = useMemo(
    () => new THREE.MeshStandardMaterial({ color: boardTheme.dark, roughness: 0.8 }),
    [boardTheme],
  );
  // The felt keeps `light`; the light triangles are a step darker so they show on it.
  const lightTriMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({ color: backgammonLightTriangleColor(boardTheme), roughness: 0.8 }),
    [boardTheme],
  );
  const whiteMat = useMemo(
    () => new THREE.MeshStandardMaterial({ color: whiteColor, roughness: 0.55 }),
    [whiteColor],
  );
  const blackMat = useMemo(
    () => new THREE.MeshStandardMaterial({ color: blackColor, roughness: 0.55 }),
    [blackColor],
  );

  const points = useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => {
        const point = i + 1;
        const [x, z] = backgammonPointXZ(point);
        // The triangle is authored in the XY plane (tip toward +y). Laying it flat
        // is a -90° turn about X, which points the tip at -z and the front face up;
        // the near row then needs a half turn about Y to point its tip at +z, back
        // across the board. (Without the flat turn the shape stood on edge as a
        // spike 3.2 units tall.) The base sits on the board EDGE, so near and far
        // triangles meet short of the midline instead of crossing it.
        const rotY = point <= 12 ? Math.PI : 0;
        const baseZ = point <= 12 ? -BACKGAMMON_EDGE_Z : BACKGAMMON_EDGE_Z;
        return { point, x, z: baseZ, rotY, mat: backgammonPointIsDark(point) ? darkMat : lightTriMat };
      }),
    [darkMat, lightTriMat],
  );

  // Signed point counts → per-point stacks. One loop, no per-color branching
  // beyond the material pick: positive is white, negative is black.
  const stacks = useMemo(() => {
    const out: Array<{ key: string; pos: [number, number, number]; mat: THREE.Material }> = [];
    for (let point = 1; point <= 24; point++) {
      const count = position.points[point - 1] ?? 0;
      if (count === 0) continue;
      const color: Turn = count > 0 ? 'w' : 'b';
      const [x, z] = backgammonPointXZ(point);
      for (let i = 0; i < Math.abs(count); i++) {
        out.push({
          key: `p${point}-${i}`,
          pos: [x, backgammonStackY(i), z],
          mat: color === 'w' ? whiteMat : blackMat,
        });
      }
    }
    return out;
  }, [position, whiteMat, blackMat]);

  const barStacks = useMemo(() => {
    const out: Array<{ key: string; pos: [number, number, number]; mat: THREE.Material }> = [];
    const push = (color: Turn, count: number, mat: THREE.Material) => {
      for (let i = 0; i < count; i++) {
        const [x, z] = backgammonBarXZ(color, i);
        out.push({ key: `bar-${color}-${i}`, pos: [x, backgammonStackY(i), z], mat });
      }
    };
    push('w', position.bar.w, whiteMat);
    push('b', position.bar.b, blackMat);
    return out;
  }, [position, whiteMat, blackMat]);

  const offStacks = useMemo(() => {
    const out: Array<{ key: string; pos: [number, number, number]; mat: THREE.Material }> = [];
    const push = (color: Turn, count: number, mat: THREE.Material) => {
      for (let i = 0; i < count; i++) {
        const [x, z] = backgammonOffXZ(color, i);
        out.push({ key: `off-${color}-${i}`, pos: [x, backgammonStackY(i), z], mat });
      }
    };
    push('w', position.off.w, whiteMat);
    push('b', position.off.b, blackMat);
    return out;
  }, [position, whiteMat, blackMat]);

  return (
    <>
      <color attach="background" args={['#1d1b26']} />
      {/* Same four-light setup as ChessBoard/LessonBoard, so every scene in the
          app reads as the same room. */}
      <ambientLight intensity={0.55} />
      <directionalLight position={[6, 7, 5]} intensity={1.0} color="#ffffff" />
      <directionalLight position={[-5, 3, 4]} intensity={0.55} color="#ffe6c2" />
      <directionalLight position={[0, -2, -6]} intensity={0.25} color="#a0b4ff" />
      <perspectiveCamera position={[0, 9, -9]} fov={45} />
      {/* Rotation locked like ChessBoard: the future point-tap overlay is a
          fixed-orientation grid, and a draggable camera desyncs it. */}
      <OrbitControls
        enablePan={false}
        enableRotate={false}
        minDistance={6}
        maxDistance={16}
        maxPolarAngle={Math.PI / 2.3}
      />
      {onOverlayTransform && (
        <ProjectionReporter plane={BACKGAMMON_PLANE} onChange={onOverlayTransform} />
      )}
      <Table theme={tableTheme} />

      {/* Frame: rounded slab matching the rounded container, like Board's. */}
      <FrameRim />
      {/* Two playing halves, tops at y=0 like the chess squares. */}
      <mesh geometry={halfGeometry} position={[-2.2, -0.075, 0]} material={lightMat} />
      <mesh geometry={halfGeometry} position={[2.2, -0.075, 0]} material={lightMat} />

      {points.map((p) => (
        <mesh
          key={`pt-${p.point}`}
          geometry={triangleGeometry}
          position={[p.x, -0.008, p.z]}
          rotation={[-Math.PI / 2, p.rotY, 0, 'YXZ']}
          material={p.mat}
        />
      ))}

      {stacks.map((c) => (
        <mesh key={c.key} geometry={checkerGeometry} position={c.pos} material={c.mat} />
      ))}
      {barStacks.map((c) => (
        <mesh key={c.key} geometry={checkerGeometry} position={c.pos} material={c.mat} />
      ))}
      {offStacks.map((c) => (
        <mesh key={c.key} geometry={checkerGeometry} position={c.pos} material={c.mat} />
      ))}
    </>
  );
}

/** The rounded outer rim, same look as the chess Board's frame but wider on
 *  the right — the off tray needs the rim. A deliberate local copy of the
 *  shape rather than an import: the two boards' frames diverge here, and
 *  sharing a private helper would couple their sizes. */
function FrameRim() {
  const frameGeometry = useMemo(() => {
    const size = BACKGAMMON_FRAME_WIDTH;
    const radius = 0.25;
    const shape = new THREE.Shape();
    const half = size / 2;
    const r = Math.min(radius, half);
    shape.moveTo(half - r, half);
    shape.lineTo(-half + r, half);
    shape.quadraticCurveTo(-half, half, -half, half - r);
    shape.lineTo(-half, -half + r);
    shape.quadraticCurveTo(-half, -half, -half + r, -half);
    shape.lineTo(half - r, -half);
    shape.quadraticCurveTo(half, -half, half, -half + r);
    shape.lineTo(half, half - r);
    shape.quadraticCurveTo(half, half, half - r, half);
    return new THREE.ExtrudeGeometry(shape, {
      depth: 0.25,
      bevelEnabled: true,
      bevelSize: 0.03,
      bevelThickness: 0.03,
      bevelSegments: 2,
    });
  }, []);
  const frameMat = useMemo(
    () => new THREE.MeshStandardMaterial({ color: '#3a3447', roughness: 0.7, metalness: 0.1 }),
    [],
  );
  return (
    <mesh
      geometry={frameGeometry}
      material={frameMat}
      position={[0, -0.3, 0]}
      rotation={[-Math.PI / 2, 0, 0]}
    />
  );
}
